import AudioToHapticDialog from "@/components/home/AudioToHapticDialog";
import { useStaggerAnimation } from "@/hooks/useStaggerAnimation";
import { cn } from "@/libs/utils";
import { useGames } from "@/api/game-api";
import { BhapticsContent } from "@/store/reducers/contents";
import { useTrans } from "@/utils/useTrans";
import { BottomUpSheet } from "@/v2-components/BottomUpSheet";
import { SUPPORTED_GAMES_HREF } from "@/v2-components/pages/games/GamesSubNav";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import { CloserIcon } from "@/v2-components/core/icon/CloserIcon";
import { OpenerIcon } from "@/v2-components/core/icon/OpenerIcon";
import { Skeleton } from "@/v2-components/ui/skeleton";
import Image from "next/image";
import Link from "next/link";
import React, { Fragment, useState } from "react";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";

const BASE_PATH = "/images/home/immersion_beyond_comparision";

/**
 * 플랫폼 카드 5장 — 카드를 열면 설명 + 게임 6장 + CTA 가 나온다.
 *
 * ## ★CTA 목적지가 **개편 이전 화면**을 가리키고 있었다 (#5648)
 *
 * 작성자 보고(2026-09-09): *"해당 게임 각각 눌렀을 때 이전 링크로 이동합니다."*
 * 세 카드가 `/games/search/?filter=…` 로 갔는데, 그건 renewal_v2 이전의 게임 검색
 * 화면이다(살아 있어서 **404 가 안 난다** — 그래서 눈으로만 보면 정상으로 보인다).
 *
 * ```
 *   PC          /games/search/?filter=pc…                    → /games/pc/games/
 *   VR          /games/search/?filter=quest,steam,psvr2,rift… → /games/vr/games/
 *   Sim Racing  /games/search/?filter=simracing…             → /games/pc/games/?genre=simracing
 *   Console     /games/vr                                     → 팝업 (이동 없음)
 *   Music/Movies /games/vr                                    → 팝업 (이동 없음)
 * ```
 *
 * 경로는 `SUPPORTED_GAMES_HREF` 에서 가져온다 — 탭 정의(`GamesSubNav`)와 갈라지지
 * 않게 한 곳만 본다. 그 파일이 이미 *"탭 정의를 복제하지 않는다"* 를 규칙으로 둔다.
 *
 * ## ★`Console` · `Music/Movies` 는 **이동하지 않고 팝업을 연다**
 *
 * 작성자 요청: *"메인에서 제공하는 팝업 제공"*. 홈(`PlayEveryWay.tsx`)이 #5516 에서
 * 같은 두 카드에 이미 그렇게 했다 — 둘 다 Audio-to-Haptics 얘기라 같은 팝업을 쓴다.
 * 여기서도 **같은 컴포넌트**(`AudioToHapticDialog`)를 쓴다.
 *
 * ⚠️ 종전 목적지가 `/games/vr` 로 **둘 다 같았다**. 즉 이 두 카드는 원래도 「제 목적지」가
 * 없었다 — VR 목록으로 보내고 있었을 뿐이다.
 */
const ITEMS: {
  hideGames?: boolean;
  title: string;
  platforms: string[];
  description: string;
  videoUrl: string;
  imageUrl: string;
  /**
   * CTA 목적지. `dialog: true` 인 카드는 **이동하지 않으므로 안 읽는다** — 그래도
   * 남긴다(팝업을 되돌릴 때 목적지를 다시 찾지 않게). 홈의 `Card` 타입과 같은 규약이다.
   */
  learnmoreUrl: string;
  /** 이동 대신 Audio-to-Haptics 팝업을 연다 (#5648). */
  dialog?: boolean;
}[] = [
  {
    title: "PC",
    platforms: ["pc"],
    description:
      "Dominate the Game. Feel Every Victory. Every bullet, every blast, every hit - TactSuit turns PC game into reality.",
    videoUrl: "https://cdn.bhaptics.com/media/01_PC_640p.mp4#t=0.001",
    imageUrl: `${BASE_PATH}/desktop_00@2x.jpg`,
    learnmoreUrl: SUPPORTED_GAMES_HREF.pc,
  },
  {
    title: "VR",
    platforms: ["quest", "steam", "psvr2", "rift"],
    imageUrl: `${BASE_PATH}/desktop_01@2x.jpg`,
    description:
      "Elevate your VR gaming experience with real-time haptic feedback. No wires, just pure immersion.",
    videoUrl: "https://cdn.bhaptics.com/media/02_VR_640p.mp4#t=0.001",
    learnmoreUrl: SUPPORTED_GAMES_HREF.vr,
  },
  {
    title: "Sim Racing",
    platforms: ["simracing"],
    imageUrl: `${BASE_PATH}/desktop_02@2x.jpg`,
    description:
      "When you wear it, You’re on track. Every bump, every turn, every surface — now felt through your body.",
    videoUrl: "https://cdn.bhaptics.com/media/03_SimRacing_640p.mp4#t=0.001",
    // `simracing` 은 `PcGenreKey` 다 — PC 목록의 장르 필터로 착지한다.
    learnmoreUrl: `${SUPPORTED_GAMES_HREF.pc}/?genre=simracing`,
  },
  {
    title: "Console",
    hideGames: true,
    platforms: ["psvr2"],
    imageUrl: `${BASE_PATH}/desktop_03@2x.jpg`,
    description:
      "Enhance your console gaming experience on PS, Xbox, Nintendo, and more with audio-based profiles.",
    videoUrl: "https://cdn.bhaptics.com/media/04_Console_640p.mp4#t=0.001",
    learnmoreUrl: "/games/vr",
    dialog: true,
  },
  {
    title: "Music/Movies",
    hideGames: true,
    platforms: [],
    imageUrl: `${BASE_PATH}/desktop_04@2x.jpg`,
    description:
      "Transforms in-game sounds, music, and movies into dynamic haptic feedback for total immersion.",
    videoUrl: "https://cdn.bhaptics.com/media/05_Music_640p.mp4#t=0.001",
    learnmoreUrl: "/games/vr",
    dialog: true,
  },
];

export default function ImmersionBeyondComparison({
  className,
}: {
  className?: string;
}) {
  const isMobile = useIsMobileView();
  return isMobile ? (
    <Mobile className={className} />
  ) : (
    <Desktop className={className} />
  );
}

function Mobile({ className }: { className?: string }) {
  const { t } = useTrans(["gnb"]);
  const { ref, className: delayClassName, delayFunc } = useStaggerAnimation({});
  /**
   * Audio-to-Haptics 팝업 (#5648) — **상태는 여기 하나**다.
   *
   * `Console` · `Music/Movies` 두 카드가 같은 팝업을 연다. 카드 안에 두면 다이얼로그가
   * 두 벌 생기고 어느 것이 열렸는지 상위에서 알 수 없다(`docs/pitfalls.md` #7).
   * 홈(`PlayEveryWay.tsx`)도 같은 이유로 부모가 갖는다.
   */
  const [athOpen, setAthOpen] = useState(false);

  return (
    <div className={cn("flex w-full flex-col items-center", className)}>
      <Title className="px-4">{t("Immersion Across Every Platform")}</Title>
      <div className="mt-6 flex w-full justify-center overflow-hidden laptop:mt-8">
        <div
          ref={ref}
          className="scrollbar-none flex flex-nowrap gap-x-4 overflow-x-scroll px-4 laptop:gap-x-5"
        >
          {ITEMS.map((item, index) => {
            return (
              <div
                key={item.title}
                className={cn("h-[400px]", delayClassName)}
                style={{ transitionDelay: delayFunc(index) }}
              >
                <MobileCard
                  hideGames={!!item?.hideGames}
                  platforms={item.platforms}
                  title={item.title}
                  description={t(item.description)}
                  imageUrl={item.imageUrl}
                  learnmoreUrl={item.learnmoreUrl}
                  onDialog={item.dialog ? () => setAthOpen(true) : undefined}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* Audio-to-Haptics 팝업 — 두 카드가 공유한다 (#5648). */}
      <AudioToHapticDialog open={athOpen} onOpenChange={setAthOpen} />
    </div>
  );
}

function Desktop({ className }: { className?: string }) {
  const { t } = useTrans(["gnb"]);
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const cardRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const { ref, className: delayClassName, delayFunc } = useStaggerAnimation({});
  /** Audio-to-Haptics 팝업 — 상태는 여기 하나다(위 `Mobile` 과 같은 이유). */
  const [athOpen, setAthOpen] = useState(false);

  const handleOpenChange = (index: number, open: boolean) => {
    setOpenedIndex(open ? index : null);

    if (open && cardRefs.current[index]) {
      setTimeout(() => {
        cardRefs.current[index]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }, 50);
    }
  };

  return (
    <div className={cn("flex w-full flex-col items-center", className)}>
      <Title className="px-4">{t("Immersion Across Every Platform")}</Title>
      <div className="mt-6 flex w-full justify-center overflow-hidden laptop:mt-8">
        <div
          ref={ref}
          className="scrollbar-none flex flex-nowrap gap-x-4 overflow-x-scroll px-4 laptop:gap-x-5"
        >
          {ITEMS.map((item, index) => {
            const opened = openedIndex === index;
            return (
              <div
                key={item.title}
                className={cn("h-[400px]", delayClassName)}
                style={{ transitionDelay: delayFunc(index) }}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
              >
                <Card
                  imageUrl={item.imageUrl}
                  opened={opened}
                  hideGames={!!item?.hideGames}
                  platforms={item.platforms}
                  onOpenChange={(open) => handleOpenChange(index, open)}
                  title={item.title}
                  description={t(item.description)}
                  videoUrl={item.videoUrl}
                  learnmoreUrl={item.learnmoreUrl}
                  onDialog={item.dialog ? () => setAthOpen(true) : undefined}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* Audio-to-Haptics 팝업 — 두 카드가 공유한다 (#5648). */}
      <AudioToHapticDialog open={athOpen} onOpenChange={setAthOpen} />
    </div>
  );
}

const Title = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <h2
    className={cn(
      "text-center text-3xl font-400 text-coolgray-90 laptop:text-5xl",
      className,
    )}
  >
    {children}
  </h2>
);

const GameShots = ({ games }: { games: BhapticsContent[] }) => {
  return (
    <Fragment>
      {games.map((game) => (
        /* 슬래시를 **붙이지 않는다** — 형제 48곳과 같은 표기다(R1 M1).
           `trailingSlash: true` 라 맨 URL 은 308 이지만 `<Link>` 는 렌더할 때
           Next 가 붙인다. 실측(2026-09-09 dev): `GameCardItem.tsx:31` 이 슬래시 없이
           적는데 그 페이지의 렌더된 href 는 **22/22 전부** 슬래시로 끝난다. */
        <Link key={game.id} href={`/games/experiences/${game.slug}`}>
          <div className="relative aspect-square overflow-hidden rounded-lg shadow-sm hover:shadow-md">
            <Image
              src={game.thumbnailLink}
              alt={game.name}
              fill
              className="object-cover"
            />
          </div>
        </Link>
      ))}
    </Fragment>
  );
};

const Card = ({
  title,
  videoUrl,
  opened,
  description,
  platforms = [],
  learnmoreUrl,
  hideGames = false,
  imageUrl,
  onOpenChange,
  onDialog,
}: React.PropsWithChildren<{
  platforms?: string[];
  imageUrl: string;
  hideGames?: boolean;
  title: string;
  description: string;
  videoUrl: string;
  learnmoreUrl: string;
  opened: boolean;
  onOpenChange: (open: boolean) => void;
  /** 이동 대신 팝업을 여는 카드면 온다 (#5648). */
  onDialog?: () => void;
}>) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { t } = useTrans(["gnb"]);
  // 프리뷰 카드 상위 6개는 착지 페이지(`learnmoreUrl`)와 같은 정렬이어야 한다 — #5064.
  // 딥링크에 `sort=` 가 없으므로 착지 페이지는 웹 기본 정렬을 쓴다 → 여기도 같은 값으로 맞춘다.
  // 기본값(`useBhapticsContents.ts` 의 `useSortBy`)을 바꿀 때 함께 바꿀 것.
  const { data = [], isLoading } = useGames({
    filters: platforms,
    sortBy: "monthlyPlayCount",
    enabled: platforms.length > 0,
  });
  const [open, setOpen] = React.useState(false);

  const learnmoreText =
    !!data.length && !hideGames
      ? t(`See {{NN}} more games`, {
          NN: data.length,
          defaultValue: `See ${data.length} more games`,
        })
      : t("Learn More");

  const games = data.filter((_, index) => index < 6);

  return (
    <Fragment>
      <BottomUpSheet
        open={open}
        onOpenChange={() => setOpen(false)}
        title={title}
      >
        <div className="mt-2">
          <p className="mt-3 text-black">{description}</p>
          <LearnMore href={learnmoreUrl} className="mt-5" onDialog={onDialog}>
            {learnmoreText}
          </LearnMore>
          {!hideGames && (
            <div className="mt-5 grid grid-cols-3 gap-4">
              <GameShots games={games} />
            </div>
          )}
        </div>
      </BottomUpSheet>
      <div
        onClick={() => {
          onOpenChange(!opened);
        }}
        className={cn(
          "relative flex size-full flex-nowrap overflow-hidden rounded-[16px] bg-coolgray-10",
        )}
        onMouseEnter={() => {
          if (opened) return;
          videoRef.current?.play();
        }}
        onMouseLeave={() => {
          if (opened) return;
          videoRef.current?.pause();
        }}
      >
        <div
          className={cn(
            "group relative h-full w-[272px] cursor-pointer transition-all",
            opened && "w-[400px]",
          )}
        >
          <video
            ref={videoRef}
            className="absolute inset-0 size-full object-cover"
            loop
            playsInline
            muted
            src={videoUrl}
          />
          <Image
            src={imageUrl}
            alt={title}
            fill
            className={cn(
              "absolute inset-0 size-full object-cover",
              opened ? "opacity-0" : "group-hover:opacity-0",
            )}
          />
          <Title className="absolute left-5 top-5 !text-2xl text-white">
            {title}
          </Title>
          {!opened && (
            <div className="absolute bottom-5 right-5">
              <OpenerIcon />
            </div>
          )}
        </div>
        {opened && (
          <Detail
            isLoading={isLoading}
            description={description}
            title={title}
            learnmoreText={learnmoreText}
            learnmoreUrl={learnmoreUrl}
            onDialog={onDialog}
          >
            {!hideGames && (
              <div className="grid grid-cols-3 gap-4 px-2">
                <GameShots games={games} />
              </div>
            )}
          </Detail>
        )}
      </div>
    </Fragment>
  );
};

const MobileCard = ({
  title,
  description,
  platforms = [],
  learnmoreUrl,
  hideGames = false,
  imageUrl,
  onDialog,
}: React.PropsWithChildren<{
  platforms?: string[];
  imageUrl: string;
  hideGames?: boolean;
  title: string;
  description: string;
  learnmoreUrl: string;
  /** 이동 대신 팝업을 여는 카드면 온다 (#5648). */
  onDialog?: () => void;
}>) => {
  const { t } = useTrans(["gnb"]);
  // 프리뷰 카드 상위 6개는 착지 페이지(`learnmoreUrl`)와 같은 정렬이어야 한다 — #5064.
  // 딥링크에 `sort=` 가 없으므로 착지 페이지는 웹 기본 정렬을 쓴다 → 여기도 같은 값으로 맞춘다.
  // 기본값(`useBhapticsContents.ts` 의 `useSortBy`)을 바꿀 때 함께 바꿀 것.
  const { data = [], isLoading } = useGames({
    filters: platforms,
    sortBy: "monthlyPlayCount",
    enabled: platforms.length > 0,
  });
  const [open, setOpen] = React.useState(false);

  const learnmoreText =
    !!data.length && !hideGames
      ? t(`See {{NN}} more games`, {
          NN: data.length,
          defaultValue: `See ${data.length} more games`,
        })
      : t("Learn More");

  const games = data.filter((_, index) => index < 6);

  return (
    <Fragment>
      <BottomUpSheet
        open={open}
        onOpenChange={() => setOpen(false)}
        title={title}
      >
        <div className="mt-2">
          <p className="mt-3 text-black">{description}</p>
          <LearnMore href={learnmoreUrl} className="mt-5" onDialog={onDialog}>
            {learnmoreText}
          </LearnMore>
          {!hideGames && (
            <div className="mt-5 grid grid-cols-3 gap-4">
              <GameShots games={games} />
            </div>
          )}
        </div>
      </BottomUpSheet>
      <div
        onClick={() => {
          setOpen(true);
        }}
        className={cn(
          "relative flex size-full cursor-pointer flex-nowrap overflow-hidden rounded-[16px] bg-coolgray-10",
        )}
      >
        <div className={cn("group relative h-full w-[272px] transition-all")}>
          <Image
            src={imageUrl}
            alt={title}
            fill
            className="absolute inset-0 size-full object-cover"
          />
          <Title className="absolute left-5 top-5 !text-2xl text-white">
            {title}
          </Title>
          <div className="absolute bottom-5 right-5">
            <OpenerIcon />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const Detail = ({
  children,
  title,
  description,
  learnmoreUrl,
  learnmoreText,
  isLoading,
  onDialog,
}: React.PropsWithChildren<{
  title: string;
  isLoading?: boolean;
  description: string;
  learnmoreUrl: string;
  learnmoreText: string;
  onDialog?: () => void;
}>) => {
  if (isLoading) {
    return (
      <div className="relative flex h-full w-auto flex-col justify-center p-5 laptop:w-[340px]">
        <Skeleton />
      </div>
    );
  }
  return (
    <div className="relative flex h-full w-auto flex-col justify-between p-5 laptop:w-[340px]">
      <div className="flex-1 overflow-hidden pb-5">
        <DetailCard title={title} description={description}>
          {children}
        </DetailCard>
      </div>

      <div className="flex items-center justify-between gap-x-10">
        <LearnMore href={learnmoreUrl} onDialog={onDialog}>
          {learnmoreText}
        </LearnMore>
        <button>
          <CloserIcon />
        </button>
      </div>
    </div>
  );
};

/**
 * CTA — **이동이거나 팝업이거나 둘 중 하나다** (#5648).
 *
 * `onDialog` 가 오면 `<Link>` 로 감싸지 않는다. `<Link>` 안의 `<button>` 은 눌리면
 * 이동하므로, 팝업만 열려는 카드를 그 안에 두면 팝업이 뜨자마자 화면이 떠난다.
 * 홈(`PlayEveryWay.tsx`)의 `UseCase` 도 같은 이유로 두 갈래다.
 */
const LearnMore = ({
  children,
  href,
  className,
  onDialog,
}: React.PropsWithChildren<{
  href: string;
  className?: string;
  onDialog?: () => void;
}>) => {
  const inner = (
    <button
      type="button"
      onClick={onDialog}
      aria-haspopup={onDialog ? "dialog" : undefined}
      className={cn(
        "flex items-center gap-x-2.5 rounded-full border border-blue-60 px-6 py-2.5",
        className,
      )}
    >
      <span className="whitespace-nowrap text-sm font-600 text-blue-60">
        {children}
      </span>
      <ChevronRightIcon className="size-4.5 text-blue-60" />
    </button>
  );
  return onDialog ? inner : <Link href={href}>{inner}</Link>;
};

const DetailCard = ({
  title,
  description,
  children,
}: React.PropsWithChildren<{ title?: string; description?: string }>) => {
  return (
    <div className="flex h-full flex-col gap-y-5">
      <div>
        {!!title && (
          <h3 className="text-2xl font-400 text-coolgray-100">{title}</h3>
        )}
        {!!description && (
          <p className="mt-3 text-base text-black">{description}</p>
        )}
      </div>

      <div className="flex-1 overflow-auto">{children}</div>
    </div>
  );
};
